"use client";

import { useState, useEffect } from "react";
import PopularPodcastItem from "../popular-podcast-item";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ArrowRightIcon } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSubscribedPodcasts } from "@/hooks/useSubscribedPodcasts";
import { useUser } from "@/lib/context/user-context";

// 用于避免 hydration 不匹配的 hook
function useHasMounted() {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  return hasMounted;
}

export default function FollowedSection() {
  const hasMounted = useHasMounted();
  const { t } = useI18n();
  const { user } = useUser();
  const { subscribedPodcasts, isLoading } = useSubscribedPodcasts();

  // 服务端和客户端初始渲染都返回 null，避免 hydration 不匹配
  if (!hasMounted) {
    return null;
  }

  // 如果未登录或没有关注的播客（且加载完成），则不显示此部分
  if (!user || (!isLoading && (!subscribedPodcasts || subscribedPodcasts.length === 0))) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <section className="my-4">
        <header className="pl-2 pb-1 sm:pb-4 flex justify-between items-center">
          <h2 className="font-sans text-xl sm:text-2xl font-bold">{t('home.following.title', 'Following')}</h2>
        </header>
        
        <div className="w-full overflow-x-auto scrollbar-hide max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-2rem)]">
          <div className="flex gap-2" style={{ width: 'fit-content' }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="flex-shrink-0 w-48">
                <div className="animate-pulse">
                  <div className="w-12 h-12 bg-muted rounded-full mb-3"></div>
                  <div className="bg-muted h-4 rounded mb-2"></div>
                  <div className="bg-muted h-3 rounded w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Map subscribed podcasts to the format expected by PopularPodcastItem
  const podcasts = subscribedPodcasts.map(podcast => {
    const isChannel = podcast.id.startsWith('UC');
    return {
      data: {
        ownerAvatarUrl: podcast.imageUrl || '',
        author: podcast.author || '',
        name: podcast.title,
        slug: podcast.id,
        defaultLevelRange: podcast.categories || []
      },
      href: isChannel ? `/channels/${podcast.id}` : `/podcast/${podcast.id}`
    };
  });

  return (
    <section className="my-4">
      <header className="pl-2 pb-1 sm:pb-4 flex justify-between items-center">
        <h2 className="font-sans text-xl sm:text-2xl font-bold">{t('home.following.title', 'Following')}</h2>
        <Button className="h-auto py-1 text-sm sm:text-normal font-normal" variant="link" asChild>
          <Link href="/library?tab=podcasts">
            <span className="text-xs sm:text-normal font-normal">{t('home.channels.viewAll', 'View all')}</span> <ArrowRightIcon className="size-3 ml-1" />
          </Link>
        </Button>
      </header>
      
      <div className="w-full overflow-x-auto scrollbar-hide max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-2rem)]">
        <div className="flex gap-2" style={{ width: 'fit-content' }}>
          {podcasts.map((item, index) => (
            <div
              key={`${item.data.slug}-${index}`}
              className="flex-shrink-0"
            >
              <PopularPodcastItem 
                podcast={item.data} 
                href={item.href}
                type={item.href.includes('/channels/') ? 'channel' : 'podcast'}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

